import Matter from "matter-js";
import * as C from "../../config/constants.js";
import {
  enforceContainmentIfDue,
  enforceMouthEscapeContainment,
  enforceWallContainment,
  resolvePhysicsStepCap,
} from "./containment.js";
import { updateMergeMotions } from "./merge-motion.js";
import { updateShrinkMotions } from "./shrink-motion.js";

/**
 * A frame this close under one step still runs it (time debt carried to the next frame), so
 * rAF jitter around 60 Hz (16.5 / 16.8 ms) gives exactly one step a frame, never 0-then-2.
 */
var STEP_EARLY_FRAC = 0.25;

/**
 * Matter always advances by the same fixed step (default 1000/60 = Matter's base delta), fed by
 * an accumulator. A varying step makes Matter's Verlet integration and the raw `body.velocity`
 * units (px per step) change frame to frame — resting piles buzz and velocity-based tuning drifts.
 * On 120 Hz screens the jar steps every other frame.
 */
export function updateSystem(system, deltaMs) {
  if (!system.engine) return;
  if (typeof system.syncEngineGravity === "function") system.syncEngineGravity();
  var rc = system.runConfig || {};
  var fallbackStepMs = 1000 / 60;
  var maxFrameMs = (rc.MAX_FRAME_DELTA_MS != null)
    ? Number(rc.MAX_FRAME_DELTA_MS)
    : (C.MAX_FRAME_DELTA_MS != null ? Number(C.MAX_FRAME_DELTA_MS) : 100);
  var fixedStepMs = (rc.FLASK_PHYSICS_STEP_MS != null)
    ? Number(rc.FLASK_PHYSICS_STEP_MS)
    : (C.FLASK_PHYSICS_STEP_MS != null
      ? Number(C.FLASK_PHYSICS_STEP_MS)
      : fallbackStepMs);
  if (!isFinite(fixedStepMs) || fixedStepMs <= 0) fixedStepMs = fallbackStepMs;
  var maxSubsteps = (rc.FLASK_PHYSICS_MAX_SUBSTEPS != null)
    ? Number(rc.FLASK_PHYSICS_MAX_SUBSTEPS)
    : (C.FLASK_PHYSICS_MAX_SUBSTEPS != null
      ? Number(C.FLASK_PHYSICS_MAX_SUBSTEPS)
      : 6);
  var safeDelta = Number(deltaMs);
  if (!isFinite(safeDelta) || safeDelta <= 0) return;
  var clampedDelta = Math.min(maxFrameMs, safeDelta);
  var stepCap = resolvePhysicsStepCap(system, maxSubsteps);
  var earlyMs = fixedStepMs * STEP_EARLY_FRAC;
  var acc = (system._physicsAccumulatorMs || 0) + clampedDelta;
  var steps = 0;
  while (acc >= fixedStepMs - earlyMs && steps < stepCap) {
    enforceMouthEscapeContainment(system, "pre");
    Matter.Engine.update(system.engine, fixedStepMs);
    enforceWallContainment(system);
    enforceMouthEscapeContainment(system, "post");
    acc -= fixedStepMs;
    steps++;
  }
  // Hit the cap (long stall): drop the backlog instead of fast-forwarding it next frame.
  if (acc > fixedStepMs - earlyMs) acc = 0;
  system._physicsAccumulatorMs = acc;
  if (steps > 0) enforceContainmentIfDue(system);
  updateMergeMotions(system);
  updateShrinkMotions(system);
}
